import React from "react";
import { Chip, CircularProgress, Tooltip } from "@mui/material";
import { CardGiftcard } from "@mui/icons-material";
import { Order } from "../../../types";
import { orderService } from "../../../services/orderService";
import { Notify, useQuickEditSave } from "./useQuickEditSave";

interface GiftsOnBillChipProps {
    order: Order;
    /** Whether quick edits are allowed; otherwise the chip is read-only. */
    editable: boolean;
    onOrderChanged: (order: Order) => void;
    notify: Notify;
}

/**
 * "Gifts shown on bill" / "Gifts hidden from bill" — whether the printed
 * bill lists the order's free gift lines. Click to switch when editable.
 */
export const GiftsOnBillChip: React.FC<GiftsOnBillChipProps> = ({ order, editable, onOrderChanged, notify }) => {
    const { saving, save } = useQuickEditSave(order.order_id, onOrderChanged, notify);
    const shown = !!order.show_gifts_on_bill;

    const toggle = async () => {
        if (saving) return;
        const next = !shown;
        const saved = await save(
            () => orderService.updateOrder(order.order_id, { show_gifts_on_bill: next }),
            next ? "Gifts will show on the bill" : "Gifts hidden from the bill",
        );
        if (!saved) notify("Couldn't change the gifts setting. Please try again.", "error");
    };

    const chip = (
        <Chip
            icon={saving ? <CircularProgress size={14} /> : <CardGiftcard fontSize="small" />}
            label={shown ? "Gifts shown on bill" : "Gifts hidden from bill"}
            size="small"
            color={shown ? "success" : "default"}
            variant="outlined"
            onClick={editable ? () => void toggle() : undefined}
        />
    );

    if (!editable) return chip;
    return (
        <Tooltip title={shown ? "Click to hide gifts from the printed bill" : "Click to show gifts on the printed bill"}>
            {chip}
        </Tooltip>
    );
};
